/**
 * The profile's centre column: what has happened with this contact, by month
 * and in order.
 *
 * Both read the same derived list (`buildEvents`), so the chart and the
 * timeline cannot disagree about a deal. A row opens the thing it names: a
 * message opens the stored message, a deal opens the dossier. A filter only
 * narrows the events already loaded; it asks the server for nothing.
 */
import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { Loader2 } from 'lucide-react'

import { formatUsd } from '@/utils/money'
import { openDeal } from './contactLinks'
import EmailViewer from './EmailViewer'
import {
  EVENT_LABELS,
  TIMELINE_FILTERS,
  agoLabel,
  daysBetween,
  formatDay,
  matchesFilter,
  type DealFact,
  type MonthBucket,
  type ProfileEvent,
  type RelationshipFacts,
  type TimelineFilter,
  count,
} from './profileFacts'
import { Card, FOCUS } from './profileUi'

// ── Deals by month ────────────────────────────────────────────────────────────

const BAR_AREA_PX = 84

export function DealsByMonth({
  deals,
  buckets,
  undated,
  earlier,
  facts,
  showFunded,
  now,
}: {
  deals: DealFact[]
  buckets: MonthBucket[]
  undated: number
  earlier: number
  facts: RelationshipFacts
  /** Whether any linked deal has a stage on record; without one, "funded" is unknown, not zero. */
  showFunded: boolean
  now: Date
}) {
  const peak = Math.max(1, ...buckets.map((b) => b.deals))
  const latest = deals.find((d) => d.at)
  const summary = buckets
    .filter((b) => b.deals > 0)
    .map((b) => `${b.label} ${b.year}: ${count(b.deals, 'deal')}`)
    .join('; ')

  return (
    <Card
      title="Deals by month"
      action={
        <span className="flex items-center gap-3 text-[11px] text-stone-400" aria-hidden="true">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-stone-500" />
            Linked deals
          </span>
          {showFunded ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-amber-400" />
              Funded
            </span>
          ) : null}
        </span>
      }
    >
      <div
        role="img"
        aria-label={`Deals by month for the last ${buckets.length} months. ${summary || 'No deal is dated in these months.'}`}
        className="flex items-end gap-1.5 border-b border-stone-800"
        style={{ height: BAR_AREA_PX + 18 }}
      >
        {buckets.map((b) => {
          const barHeight = b.deals === 0 ? 0 : Math.max(6, Math.round((b.deals / peak) * BAR_AREA_PX))
          const fundedHeight = b.funded === 0 ? 0 : Math.max(4, Math.round((b.funded / b.deals) * barHeight))
          return (
            <div key={b.key} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1">
              <span className="text-[11px] tabular-nums text-stone-300">{b.deals === 0 ? '' : b.deals}</span>
              <span
                className="flex w-full max-w-[30px] flex-col justify-end overflow-hidden rounded-t bg-stone-500"
                style={{ height: barHeight }}
              >
                {showFunded && fundedHeight > 0 ? <span className="block w-full bg-amber-400" style={{ height: fundedHeight }} /> : null}
              </span>
            </div>
          )
        })}
      </div>
      <div className="mt-1.5 flex gap-1.5" aria-hidden="true">
        {buckets.map((b, index) => (
          <span key={b.key} className="min-w-0 flex-1 text-center text-[10.5px] text-stone-500">
            {b.label}
            {index === 0 || b.key.endsWith('-01') ? <span className="block text-[9.5px] text-stone-600">{b.year}</span> : null}
          </span>
        ))}
      </div>
      <p className="mt-3 text-[12px] leading-snug text-stone-300">
        {latest?.at ? (
          <>
            Latest deal {formatDay(latest.at)}, {agoLabel(Math.max(0, daysBetween(latest.at, now)))}.{' '}
          </>
        ) : null}
        {count(facts.dealsLast12Months, 'deal')} in the last twelve months
        {facts.volumeLast12Months !== null ? `, ${formatUsd(facts.volumeLast12Months)} where an amount is on record` : ''}.
      </p>
      <p className="mt-1 text-[11px] leading-snug text-stone-500">
        Each deal is dated by the first message that tied this contact to it.
        {undated > 0 ? ` ${count(undated, 'deal')} ${undated === 1 ? 'has' : 'have'} no date and ${undated === 1 ? 'is' : 'are'} not charted.` : ''}
        {earlier > 0 ? ` ${count(earlier, 'earlier deal')} fall${earlier === 1 ? 's' : ''} before the months shown.` : ''}
      </p>
    </Card>
  )
}

// ── Timeline ──────────────────────────────────────────────────────────────────

const PAGE = 25

function dayLabel(event: ProfileEvent): { day: string; year: string } | null {
  if (!event.at) return null
  return {
    day: event.at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    year: String(event.at.getFullYear()),
  }
}

function emptyMessage(filter: TimelineFilter, anything: boolean): string {
  if (!anything) return 'Nothing is on record for this contact yet. Messages and deals appear here once the contact is on a deal’s correspondence.'
  switch (filter) {
    case 'email':
      return 'No messages are on record for this contact.'
    case 'deal':
      return 'No deals are linked to this contact.'
    case 'people':
      return 'No calls or meetings are logged for this contact.'
    case 'note':
      return 'No notes yet. Use Log a note to add one.'
    default:
      return 'Nothing is on record for this contact yet.'
  }
}

function Row({ event, onOpenMessage }: { event: ProfileEvent; onOpenMessage: (ingestionId: string) => void }) {
  const when = dayLabel(event)
  const dealId = event.kind === 'deal' ? event.dealId : null
  const message = event.kind === 'email' ? event.ingestionId : null
  const action = dealId ? () => openDeal(dealId) : message ? () => onOpenMessage(message) : null
  const title = <span className="line-clamp-2 break-words">{event.title}</span>
  return (
    <li className="grid grid-cols-[52px_62px_minmax(0,1fr)] gap-x-3 border-t border-stone-800/80 py-2.5 sm:grid-cols-[52px_62px_minmax(0,1fr)_auto]">
      <div className="tabular-nums text-stone-400">
        {when ? (
          <>
            <div className="text-[12px] text-stone-300">{when.day}</div>
            <div className="text-[10.5px] text-stone-600">{when.year}</div>
          </>
        ) : (
          <div className="text-[12px] text-stone-600" title="No date is recorded for this entry">
            No date
          </div>
        )}
      </div>
      <span
        className={clsx(
          'h-fit self-start rounded border px-1 py-[3px] text-center font-mono text-[9.5px] uppercase tracking-[0.1em]',
          event.kind === 'deal' ? 'border-amber-400/40 text-amber-200' : 'border-stone-700 text-stone-400',
        )}
      >
        {EVENT_LABELS[event.kind]}
      </span>
      <div className="min-w-0 text-[13px] text-stone-100">
        {action ? (
          <button
            type="button"
            onClick={action}
            title={dealId ? 'Open the deal' : 'Open the stored message'}
            className={`block w-full rounded text-left hover:text-amber-200 ${FOCUS}`}
          >
            {title}
          </button>
        ) : (
          title
        )}
        {event.detail ? <div className="mt-0.5 line-clamp-2 text-[12px] text-stone-500">{event.detail}</div> : null}
      </div>
      {event.tag ? (
        <span className="col-start-3 mt-1 text-[11.5px] text-stone-400 sm:col-start-4 sm:mt-0 sm:text-right">{event.tag}</span>
      ) : null}
    </li>
  )
}

export function Timeline({
  events,
  gap,
  notes,
}: {
  events: ProfileEvent[]
  /** "38 days with no activity" and what the last touch was, when there is a gap to flag. */
  gap: { days: number; since: Date; phrase: string } | null
  /** Where the activity trail the notes come from stands. */
  notes: { pending: boolean; error: string | null }
}) {
  const [filter, setFilter] = useState<TimelineFilter>('all')
  const [shown, setShown] = useState(PAGE)
  const [openMessage, setOpenMessage] = useState<string | null>(null)
  // A different filter starts again from the newest.
  useEffect(() => setShown(PAGE), [filter])

  const counts = Object.fromEntries(
    TIMELINE_FILTERS.map((f) => [f.key, events.filter((e) => matchesFilter(e.kind, f.key)).length]),
  ) as Record<TimelineFilter, number>
  const visible = events.filter((e) => matchesFilter(e.kind, filter))
  const dated = visible.filter((e) => e.at)
  const undated = visible.filter((e) => !e.at)
  const page = [...dated, ...undated].slice(0, shown)
  const hidden = visible.length - page.length

  return (
    <Card title="Everything with this contact, newest first">
      <div role="group" aria-label="Filter the timeline" className="mb-3 flex flex-wrap gap-1.5">
        {TIMELINE_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            aria-pressed={filter === f.key}
            onClick={() => setFilter(f.key)}
            className={clsx(
              'rounded-full border px-2.5 py-1 text-[11.5px]',
              FOCUS,
              filter === f.key
                ? 'border-amber-400/60 bg-amber-400/10 text-amber-200'
                : 'border-stone-700 text-stone-300 hover:border-stone-500',
            )}
          >
            {f.label} <span className="tabular-nums text-stone-500">{counts[f.key]}</span>
          </button>
        ))}
      </div>
      {gap && (filter === 'all' || filter === 'email' || filter === 'deal' || filter === 'people') ? (
        <div
          role="note"
          className="mb-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 rounded-lg border border-dashed border-amber-400/40 px-3 py-2 text-[12.5px] text-amber-200"
        >
          <span className="font-semibold">{gap.days} days with no activity</span>
          <span className="text-amber-200/70">
            Last touch: {gap.phrase}, {formatDay(gap.since)}
          </span>
        </div>
      ) : null}
      {notes.pending ? (
        <p role="status" className="mb-1 flex items-center gap-1.5 text-[11px] text-stone-500">
          <Loader2 size={11} className="motion-safe:animate-spin" aria-hidden /> Reading notes…
        </p>
      ) : notes.error ? (
        <p role="alert" className="mb-1 text-[11px] text-red-300">
          Notes could not be read: {notes.error}
        </p>
      ) : null}
      {page.length === 0 ? (
        <p className="py-4 text-[12.5px] text-stone-500">{emptyMessage(filter, events.length > 0)}</p>
      ) : (
        <ol aria-label="Timeline">
          {page.map((event) => (
            <Row key={event.id} event={event} onOpenMessage={setOpenMessage} />
          ))}
        </ol>
      )}
      {hidden > 0 ? (
        <button
          type="button"
          onClick={() => setShown((n) => n + PAGE)}
          className={clsx('mt-2 w-full rounded-md border border-stone-800 py-1.5 text-[12px] text-stone-300 hover:border-stone-600', FOCUS)}
        >
          Show {Math.min(PAGE, hidden)} more ({hidden} older)
        </button>
      ) : null}
      {openMessage ? <EmailViewer ingestionId={openMessage} onClose={() => setOpenMessage(null)} /> : null}
    </Card>
  )
}
